import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "environment";
import { Observable, map, firstValueFrom } from "rxjs";
import { CredentialsUpdate } from "../interfaces/credentials-updates.interface";
import { DropdownItem } from "../interfaces/dropdown-item.interface";
import { ListResponse } from "../interfaces/list-response.interface";
import { UserPosition } from "../interfaces/user-position.interface";
import {
  SecurityGroupUser,
  User,
  UserFacility,
  UserListItem,
  UserPermissions,
  UserRateDetail,
  UserRateItem,
} from "../interfaces/user.interface";
import { sanitizeParams } from "../sanitize-params";
import { format } from "date-fns";

@Injectable({ providedIn: "root" })
export class UserClient {
  endpoint = "user";
  constructor(private httpClient: HttpClient) {}

  getUsersByFacility(facilityId: string): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/${this.endpoint}/get-users-by-facility/${facilityId}`
    );
  }

  getUsers(filters: {
    corpId?: string;
    searchBy: string;
    keyword?: string;
    includeDisabled?: boolean;
    includeDeleted?: boolean;
    includeInternal?: boolean;
    page?: number;
    length?: number;
    sortBy?: string;
    sortDir?: string;
    facilityId?: string;
  }): Observable<ListResponse<UserListItem>> {
    const params = sanitizeParams(filters) as Record<string, any>;

    return this.httpClient.get<ListResponse<UserListItem>>(
      `${environment.endpoint}/${this.endpoint}/list`,
      { params }
    );
  }

  getUser(userId: string): Observable<User> {
    return this.httpClient.get<User>(
      `${environment.endpoint}/${this.endpoint}/get/${userId}`
    );
  }

  getUserRates(args: {
    userId: string;
    corpId: string;
  }): Observable<UserRateItem[]> {
    return this.httpClient.get<UserRateItem[]>(
      `${environment.endpoint}/${this.endpoint}/get-user-rates/${args.corpId}/${args.userId}`
    );
  }

  getUserFacilities(userId: string): Observable<UserFacility[]> {
    return this.httpClient.get<UserFacility[]>(
      `${environment.endpoint}/${this.endpoint}/facility-list/${userId}`
    );
  }

  createUser(user: User): Observable<User> {
    return this.httpClient.post<User>(
      `${environment.endpoint}/${this.endpoint}/add`,
      user
    );
  }

  updateUser(user: User): Observable<User> {
    return this.httpClient.put<User>(
      `${environment.endpoint}/${this.endpoint}/edit`,
      user
    );
  }

  deleteUser(userId: string) {
    return this.httpClient.delete(
      `${environment.endpoint}/${this.endpoint}/delete/${userId}`
    );
  }

  enableUser(userId: string) {
    return this.httpClient.put(
      `${environment.endpoint}/${this.endpoint}/enabled/${userId}`,
      {}
    );
  }

  disableUser(userId: string) {
    return this.httpClient.put(
      `${environment.endpoint}/${this.endpoint}/disabled/${userId}`,
      {}
    );
  }

  resetPassword(userId: string) {
    // come back and correct endpoint
    return this.httpClient.delete(
      `${environment.endpoint}/${this.endpoint}/enabled/${userId}`
    );
  }

  resendInvite(userId: string) {
    return this.httpClient.put(
      `${environment.endpoint}/${this.endpoint}/invite/${userId}`,
      {}
    );
  }

  isValidPhoneNumber(phoneNumber: string, id?: string): Observable<boolean> {
    const params = sanitizeParams({ phoneNumber, id }) as Record<string, any>;

    return this.httpClient
      .get(`${environment.endpoint}/${this.endpoint}/validate-phone`, {
        params: params,
        observe: "response",
      })
      .pipe(map((response) => response.status === 204));
  }

  isValidUserId(userId: string, id?: string): Observable<boolean> {
    const params = sanitizeParams({ userId, id }) as Record<string, any>;

    return this.httpClient
      .get(`${environment.endpoint}/${this.endpoint}/validate-userid`, {
        params: params,
        observe: "response",
      })
      .pipe(map((response) => response.status === 204));
  }

  isValidEmail(email: string, id?: string): Observable<boolean> {
    const params = sanitizeParams({ email, id }) as Record<string, any>;

    return this.httpClient
      .get(`${environment.endpoint}/${this.endpoint}/validate-email`, {
        params: params,
        observe: "response",
      })
      .pipe(map((response) => response.status === 204));
  }

  getUsersBySecurityGroupId(
    securityGroupId: string
  ): Observable<SecurityGroupUser[]> {
    return this.httpClient.get<SecurityGroupUser[]>(
      `${environment.endpoint}/${this.endpoint}/get-by-security-group/${securityGroupId}`
    );
  }

  getUserPermissions(
    userId: string,
    facilityId: string
  ): Observable<UserPermissions> {
    return this.httpClient.get<UserPermissions>(
      `${environment.endpoint}/${this.endpoint}/get-user-permissions/${userId}/${facilityId}`
    );
  }

  updateUserPermissions(object: {
    userid: string;
    facilityid: string;
    features: [];
  }) {
    return this.httpClient.post<{
      userid: string;
      facilityid: string;
      features: [];
    }>(
      `${environment.endpoint}/${this.endpoint}/manage-user-permissions`,
      object
    );
  }

  changeHomeFacility(userId: string, facilityId: string) {
    return this.httpClient.put(
      `${environment.endpoint}/${this.endpoint}/change-home-facility/${userId}/${facilityId}`,
      {}
    );
  }

  getTimeInFacilityLookup(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/user/get-contract-data`
    );
  }

  getEmploymentStatusLookup(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/employment-status`
    );
  }

  getExemptionsLookup(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/exemptions`
    );
  }

  getDisciplinesLookups(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/disciplines`
    );
  }

  getRateDisciplineLookups(
    userid: string | undefined
  ): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/get-rate-disciplines/${userid}`
    );
  }

  getJobTitleLookups(userId: string | undefined): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/job-titles/${userId}`
    );
  }
  
  getJobTitles(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/job-titles`
    );
  }

  getJobTitleCodeLookups(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/job-title-codes`
    );
  }

  getRateExceptionLookups(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/rate-exceptions`
    );
  }

  addUserRate(userRate: UserRateDetail) {
    return this.httpClient.post<UserRateDetail>(
      `${environment.endpoint}/${this.endpoint}/add-rate`,
      userRate
    );
  }

  editUserRate(userRate: UserRateDetail) {
    return this.httpClient.put<UserRateDetail>(
      `${environment.endpoint}/${this.endpoint}/edit-rate`,
      userRate
    );
  }

  getUserLatestRate(
    userId: string,
    corpId: string
  ): Observable<UserRateDetail> {
    return this.httpClient.get<UserRateDetail>(
      `${environment.endpoint}/${this.endpoint}/get-user-last-rate/${corpId}/${userId}`
    );
  }

  getUserRateDetail(rateId: string): Observable<UserRateDetail> {
    return this.httpClient.get<UserRateDetail>(
      `${environment.endpoint}/${this.endpoint}/get-rate-details/${rateId}`
    );
  }

  updateUserFacilitySecurityGroup(
    userId: string,
    facilityId: string,
    securityGroupId: string
  ) {
    return this.httpClient.put(
      `${environment.endpoint}/${this.endpoint}/change-security-group/${userId}/${facilityId}/${securityGroupId}`,
      {}
    );
  }

  addFacilities(userId: string, facilityIds: string[]) {
    return this.httpClient.post(
      `${environment.endpoint}/${this.endpoint}/add-facilities`,
      {
        userid: userId,
        facilitids: facilityIds,
      }
    );
  }

  addFacilitySecurityGroups(
    userId: string,
    facilityGroups: { facilityid: string; securitygroupid: string }[]
  ) {
    return this.httpClient.post(
      `${environment.endpoint}/${this.endpoint}/add-update-facilities-with-group`,
      {
        userid: userId,
        facilityids: facilityGroups,
      }
    );
  }

  removeFacility(userId: string, facilityId: string) {
    return this.httpClient.put(
      `${environment.endpoint}/${this.endpoint}/remove-facility/${userId}/${facilityId}`,
      {}
    );
  }

  addRateException(rateException: {
    usercorprateid: string;
    facilityid: string;
    stdrate: number;
  }) {
    return this.httpClient.post(
      `${environment.endpoint}/${this.endpoint}/add-rate-exception`,
      rateException
    );
  }

  removeRateException(rateExceptionId: string) {
    return this.httpClient.delete(
      `${environment.endpoint}/${this.endpoint}/delete-rate-exception/${rateExceptionId}`
    );
  }

  getUserCredentials(userId: string) {
    return this.httpClient.get<any>(
      `${environment.endpoint}/${this.endpoint}/credentials/${userId}`
    );
  }

  updateUserCredentials(credentials: any) {
    return this.httpClient.put(
      `${environment.endpoint}/${this.endpoint}/edit-credential`,
      this.transformCredentialsToApi(credentials)
    );
  }

  addUserCredentials(credentials: any) {
    return this.httpClient.post(
      `${environment.endpoint}/${this.endpoint}/add-credential`,
      this.transformCredentialsToApi(credentials)
    );
  }

  deleteUserCredentials(credentialsId: string) {
    return this.httpClient.delete(
      `${environment.endpoint}/${this.endpoint}/delete-credential/${credentialsId}`
    );
  }

  getUserProviderNumbers(userId: string) {
    return this.httpClient.get<any>(
      `${environment.endpoint}/${this.endpoint}/providernumbers/${userId}`
    );
  }

  updateUserProviderNumbers(providerNumbers: any) {
    return this.httpClient.put(
      `${environment.endpoint}/${this.endpoint}/edit-providernumber`,
      providerNumbers
    );
  }

  addUserProviderNumbers(providerNumbers: any) {
    return this.httpClient.post(
      `${environment.endpoint}/${this.endpoint}/add-providernumber`,
      providerNumbers
    );
  }

  deleteProviderNumbers(providerNumbersId: string) {
    return this.httpClient.delete(
      `${environment.endpoint}/${this.endpoint}/delete-providernumber/${providerNumbersId}`
    );
  }

  getUserLicense(userId: string) {
    return this.httpClient
      .get<any>(`${environment.endpoint}/${this.endpoint}/licenses/${userId}`)
      .pipe(
        map((licenses) =>
          licenses.map((license: any) =>
            this.transformLicenseStatusToDisplay(license)
          )
        )
      );
  }

  deleteUserLicense(licenseId: string) {
    return this.httpClient.delete(
      `${environment.endpoint}/${this.endpoint}/delete-license/${licenseId}`
    );
  }

  updateUserLicense(license: any) {
    return this.httpClient
      .put(
        `${environment.endpoint}/${this.endpoint}/edit-license`,
        this.transformLicenseStatusToApi(license)
      )
      .pipe(
        map((license: any) => this.transformLicenseStatusToDisplay(license))
      );
  }

  addUserLicense(license: any) {
    return this.httpClient
      .post(
        `${environment.endpoint}/${this.endpoint}/add-license`,
        this.transformLicenseStatusToApi(license)
      )
      .pipe(
        map((license: any) => this.transformLicenseStatusToDisplay(license))
      );
  }

  getUserPosition(corpId: string, userId: string) {
    return this.httpClient.get<any>(
      `${environment.endpoint}/${this.endpoint}/get-user-position/${corpId}/${userId}`
    );
  }

  saveUserPosition(userPosition: UserPosition) {
    return this.httpClient.post(
      `${environment.endpoint}/${this.endpoint}/manage-user-position`,
      userPosition
    );
  }

  userNPICheck(npi: string): Observable<CredentialsUpdate> {
    return this.httpClient.get<CredentialsUpdate>(
      `${environment.endpoint}/useresignature/npi-check/${npi}`
    );
  }

  canUserEditTherapyBilling(
    date: string,
    disciplineId: number
  ): Observable<boolean> {
    return this.httpClient.get<boolean>(
      `${environment.endpoint}/${this.endpoint}/Get-User-Permission-Therapy-Billing/`,
      {
        params: new HttpParams()
          .set("therapydate", date)
          .set("disciplineid", disciplineId),
      }
    );
  }

  private transformLicenseStatusToDisplay(license: any) {
    return {
      ...license,
      status: license.status === "C" ? "Current" : "Expired",
    };
  }

  private transformCredentialsToApi(credentials: any) {
    return {
      ...credentials,
      effectivedate: credentials.effectivedate ? format(new Date(credentials.effectivedate), "yyyy-MM-dd") : undefined,
      expirationdate: credentials.expirationdate ? format(new Date(credentials.expirationdate), "yyyy-MM-dd") : undefined,
    };
  }

  private transformLicenseStatusToApi(license: any) {
    return {
      ...license,
      verifieddate: license.verifieddate ? format(new Date(license.verifieddate), "yyyy-MM-dd") : undefined,
      expirationdate: license.expirationdate ? format(new Date(license.expirationdate), "yyyy-MM-dd") : undefined,
      status: license.status === "Current" ? "C" : "E",
    };
  }

  updateLatestCorpFacility(corporationId: string, facilityId: string): Promise<any> {
    const url = `${environment.endpoint}/${this.endpoint}/update-user-corporation?corpId=${corporationId}&facilityId=${facilityId}`;
    return firstValueFrom(
      this.httpClient
        .put<{ responseData: any }>(url, {})
        .pipe(map((response) => response.responseData || []))
    );
  }
}
